import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { map, switchMap, catchError, withLatestFrom } from 'rxjs/operators';
import { SecurityGroupApiService } from './security-group-api.service';
import * as securityGroupActions from './security-group.actions';
import * as securityGroupSelectors from './security-group.selectors';
import * as fromRouting from '../../../core/store/routing';
import * as fromAuth from '../../../authentication/store/auth';
import { GUID_EMPTY } from '@app/core/constants';

@Injectable()
export class SecurityGroupEffects {
  constructor(
    private actions$: Actions,
    private api: SecurityGroupApiService,
    private store$: Store<any>
  ) {}

  getSecurityGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(securityGroupActions.getSecurityGroups, securityGroupActions.updateQueryParams),
      withLatestFrom(this.store$.select(fromAuth.selectUserContext), this.store$.select(securityGroupSelectors.selectQueryParams)),
      switchMap(([, userContext, queryParams]) => {
        if (!userContext?.organizationUId) {
          return [];
        }

        return this.api.getSecurityGroups(userContext.organizationUId, queryParams).pipe(
          map(results => securityGroupActions.loadSecurityGroups({ results })),
          catchError(err => of(securityGroupActions.setError({ err })))
        );
      })
    )
  );

  setSelectedSecurityGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(securityGroupActions.setSelectedSecurityGroup),
      switchMap(({ uId }) => [
        securityGroupActions.setSelectedSecurityGroupComplete({ uId }),
        fromRouting.go({
          routeData: { path: ['management', 'security', 'groups', uId] }
        })
      ])
    )
  );

  cancelEditSecurityGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(securityGroupActions.cancelEditSecurityGroup),
      switchMap(() => [securityGroupActions.cancelEditSecurityGroupComplete(), fromRouting.back()])
    )
  );

  deleteSelectedSecurityGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(securityGroupActions.deleteSelectedSecurityGroup),
      withLatestFrom(
        this.store$.select(securityGroupSelectors.selectSecurityGroupForDelete),
        this.store$.select(securityGroupSelectors.selectQueryParams),
        this.store$.select(fromRouting.selectCurrentUrl),
        this.store$.select(fromAuth.selectUserContext)
      ),
      switchMap(([, securityGroupToDelete, queryParams, url, userContext]) => {
        return this.api.deleteSecurityGroup(userContext.organizationUId, securityGroupToDelete).pipe(
          switchMap(res => this.api.getSecurityGroups(userContext.organizationUId, queryParams)),
          map(results => {
            if (url.includes(`/security/groups/${securityGroupToDelete.uId}`)) {
              return fromRouting.back();
            }

            if (results.skip >= results.totalCount) {
              return securityGroupActions.updateQueryParams({ updates: { skip: queryParams.skip - queryParams.take } });
            } else {
              return securityGroupActions.loadSecurityGroups({ results });
            }
          }),
          catchError(err => of(securityGroupActions.setError({ err })))
        );
      })
    )
  );

  saveSecurityGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(securityGroupActions.saveSecurityGroup),
      withLatestFrom(this.store$.select(fromAuth.selectUserContext)),
      switchMap(([{ securityGroup }, userContext]) =>
        securityGroup.uId === GUID_EMPTY
          ? this.api.createSecurityGroup(userContext.organizationUId, securityGroup).pipe(
              switchMap(res => [
                securityGroupActions.removeSecurityGroup({ uId: GUID_EMPTY }),
                securityGroupActions.addSecurityGroup({ securityGroup: res }),
                securityGroupActions.setSelectedSecurityGroup({ uId: res.uId }),
                securityGroupActions.showSecurityGroupForm({ showSecurityGroupForm: false })
              ]),
              catchError(err => of(securityGroupActions.setError({ err })))
            )
          : this.api.updateSecurityGroup(userContext.organizationUId, securityGroup).pipe(
              switchMap(res => [
                securityGroupActions.updateSecurityGroup({ securityGroup: res }),
                securityGroupActions.showSecurityGroupForm({ showSecurityGroupForm: false })
              ]),
              catchError(err => of(securityGroupActions.setError({ err })))
            )
      )
    )
  );

  getSecurityGroupMembers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(securityGroupActions.getSelectedSecurityGroupMembers),
      withLatestFrom(
        this.store$.select(securityGroupSelectors.selectSelectedSecurityGroup),
        this.store$.select(fromAuth.selectUserContext)
      ),
      switchMap(([action, securityGroup, userContext]) =>
        this.api.getSecurityGroupMembers(userContext.organizationUId, securityGroup.uId, action.params).pipe(
          map(results => securityGroupActions.loadSelectedSecurityGroupMembers({ results })),
          catchError(err => of(securityGroupActions.setError({ err })))
        )
      )
    )
  );

  getSecurityGroupPermissionSets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(securityGroupActions.getPermissionSets),
      withLatestFrom(
        this.store$.select(securityGroupSelectors.selectSelectedSecurityGroup),
        this.store$.select(fromAuth.selectUserContext)
      ),
      switchMap(([, securityGroup, userContext]) =>
        this.api.getSecurityGroupPermissionSets(userContext.organizationUId, securityGroup.uId).pipe(
          map(permissionSets => securityGroupActions.loadPermissionSets({ permissionSets })),
          catchError(err => of(securityGroupActions.setError({ err })))
        )
      )
    )
  );

  saveSecurityGroupPermissionSets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(securityGroupActions.savePermissionSets),
      withLatestFrom(
        this.store$.select(securityGroupSelectors.selectSelectedSecurityGroup),
        this.store$.select(fromAuth.selectUserContext)
      ),
      switchMap(([action, securityGroup, userContext]) =>
        this.api.updateSecurityGroupPermissionSets(userContext.organizationUId, securityGroup.uId, action.permissionSets).pipe(
          map(() => securityGroupActions.savePermissionSetsComplete()),
          catchError(err => of(securityGroupActions.setError({ err })))
        )
      )
    )
  );

  addMembers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(securityGroupActions.addMembers),
      withLatestFrom(
        this.store$.select(securityGroupSelectors.selectSelectedSecurityGroup),
        this.store$.select(fromAuth.selectUserContext),
        this.store$.select(securityGroupSelectors.selectMemberListQueryParams)
      ),
      switchMap(([action, securityGroup, userContext, params]) =>
        this.api.addSecurityGroupMembers(userContext.organizationUId, securityGroup.uId, action.users).pipe(
          switchMap(() => [securityGroupActions.addMembersComplete(), securityGroupActions.getSelectedSecurityGroupMembers({ params })]),
          catchError(err => of(securityGroupActions.setError({ err })))
        )
      )
    )
  );

  removeMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(securityGroupActions.removeMember),
      withLatestFrom(
        this.store$.select(securityGroupSelectors.selectSelectedSecurityGroup),
        this.store$.select(fromAuth.selectUserContext),
        this.store$.select(securityGroupSelectors.selectMemberListQueryParams)
      ),
      switchMap(([action, securityGroup, userContext, params]) =>
        this.api.removeSecurityGroupMember(userContext.organizationUId, securityGroup.uId, action.uId).pipe(
          switchMap(() => [securityGroupActions.removeMemberComplete(), securityGroupActions.getSelectedSecurityGroupMembers({ params })]),
          catchError(err => of(securityGroupActions.setError({ err })))
        )
      )
    )
  );

  updateWhereParam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(securityGroupActions.searchSecurityGroups, securityGroupActions.changeTypeFilter),
      withLatestFrom(
        this.store$.select(securityGroupSelectors.selectSearchTerm),
        this.store$.select(securityGroupSelectors.selectSecurityGroupTypeFilter)
      ),
      map(([, searchTerm, filterType]) => {
        const trimmedSearchTerm = searchTerm?.trim();

        const search = trimmedSearchTerm?.length
          ? `DisplayName.ToLower().Contains("${trimmedSearchTerm.replace('"', '\\"').toLocaleLowerCase()}") && `
          : '';

        const type = filterType !== null ? `Type=${filterType}` : 'Type!=2';

        const where = search.concat(type);

        return securityGroupActions.updateQueryParams({ updates: { where } });
      })
    )
  );
}
